/**
 * Where the provider sends the browser back.
 *
 * One route for all three providers, because the redirect URI has to be a
 * single fixed string: each provider matches it exactly against what was
 * registered in their console, so a per-provider path would mean registering
 * one per provider and a per-tenant path would mean registering one per
 * tenant, forever.
 *
 * Which provider a callback belongs to therefore cannot come from the URL, and
 * this screen does not try to work it out. The `state` names it and the server
 * reads it there. A first draft of this file guessed "probably Google", which
 * would have broken Microsoft and Apple sign-ins every single time.
 *
 * The screen does almost nothing, and that is deliberate: it reads two query
 * parameters, hands them to the server, and shows a message if that fails. All
 * the deciding happens where it can be tested without a browser.
 *
 * **The parameters are cleared from the URL on success.** A callback URL
 * carrying a used `code` sits in browser history and gets pasted into support
 * tickets. It is spent — the server made it single-use — but leaving it there
 * invites somebody to try, and a page that reloads and fails for no visible
 * reason is worse than one that cannot.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, LoadingState } from "@hardauto/core-ui";

import { NAMESPACE } from "../i18n";
import { useSession } from "../session/session";

type Outcome = "working" | "failed" | "refused";

export interface AuthCallbackScreenProps {
  /** Called once a session exists. The app decides where to go. */
  onSignedIn: () => void;
  /** Where "try another way" goes. */
  signInHref: string;
}

export function AuthCallbackScreen({ onSignedIn, signInHref }: AuthCallbackScreenProps) {
  const { t } = useTranslation(NAMESPACE);
  const session = useSession();
  const [outcome, setOutcome] = useState<Outcome>("working");
  // Guards against React running the effect twice in development's strict
  // mode. The state is single-use on the server, so the second attempt would
  // fail and the user would see an error after a sign-in that worked.
  const attempted = useRef(false);

  const complete = useCallback(async () => {
    const parameters = new URLSearchParams(window.location.search);
    const code = parameters.get("code");
    const state = parameters.get("state");
    // The provider refused, or the user pressed cancel on the consent screen.
    // `error=access_denied` is the normal, non-exceptional case, and it is
    // not a failure worth an alarming message.
    if (parameters.get("error") || !code || !state) {
      setOutcome(parameters.get("error") === "access_denied" ? "refused" : "failed");
      return;
    }

    try {
      const status = await session.completeSocial(code, state);
      // Cleared before navigating, so a spent code does not stay in history.
      window.history.replaceState({}, "", window.location.pathname);
      if (status === "authenticated") onSignedIn();
      // `mfa_required` needs nothing here: the session provider has the
      // challenge, and the app's own guard renders the MFA form.
    } catch {
      setOutcome("failed");
    }
  }, [onSignedIn, session]);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    void complete();
  }, [complete]);

  if (outcome === "working") {
    return (
      <Card title={t("social.completing")}>
        <LoadingState label={t("social.completing")} />
      </Card>
    );
  }

  return (
    <Card title={t(outcome === "refused" ? "social.cancelled" : "social.failed")}>
      <Alert tone={outcome === "refused" ? "info" : "danger"} title={t("social.whatNow")}>
        {t(outcome === "refused" ? "social.cancelledBody" : "social.failedBody")}
      </Alert>
      <Button
        onClick={() => {
          window.location.assign(signInHref);
        }}
      >
        {t("social.backToSignIn")}
      </Button>
    </Card>
  );
}
